import { useState, useRef } from 'react'
import toast from 'react-hot-toast'
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'
import api from '../services/api'
import Spinner from './Spinner'

interface ImageUploadProps {
  category?: 'posts' | 'avatars' | 'banners'
  maxFiles?: number
  onUploadComplete?: (urls: string[]) => void
  onUploadStart?: () => void
  className?: string
  previewClassName?: string
  multiple?: boolean
}

const ImageUpload = ({
  category = 'posts',
  maxFiles = 10,
  onUploadComplete,
  onUploadStart,
  className = '',
  previewClassName = '',
  multiple = true,
}: ImageUploadProps) => {
  const [uploading, setUploading] = useState(false)
  const [previews, setPreviews] = useState<string[]>([])
  const [files, setFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])

    if (selectedFiles.length === 0) return

    // Validate file count
    const totalFiles = files.length + selectedFiles.length
    if (totalFiles > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`)
      return
    }

    // Validate file types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    const invalidFiles = selectedFiles.filter((file) => !allowedTypes.includes(file.type))

    if (invalidFiles.length > 0) {
      toast.error('Only JPG, PNG, GIF, and WebP images are allowed')
      return
    }

    // Validate file sizes (10MB max)
    const maxSize = 10 * 1024 * 1024 // 10MB
    const oversizedFiles = selectedFiles.filter((file) => file.size > maxSize)

    if (oversizedFiles.length > 0) {
      toast.error('File size must be less than 10MB')
      return
    }

    // Create preview URLs
    const newPreviews = selectedFiles.map((file) => URL.createObjectURL(file))

    setFiles([...files, ...selectedFiles])
    setPreviews([...previews, ...newPreviews])
  }

  const removeFile = (index: number) => {
    // Revoke object URL to free memory
    URL.revokeObjectURL(previews[index])

    const newFiles = files.filter((_, i) => i !== index)
    const newPreviews = previews.filter((_, i) => i !== index)

    setFiles(newFiles)
    setPreviews(newPreviews)
  }

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload')
      return
    }

    setUploading(true)
    onUploadStart?.()

    try {
      const uploadedUrls: string[] = []

      if (files.length === 1) {
        // Single file upload
        const formData = new FormData()
        formData.append('file', files[0])
        formData.append('category', category)

        const response = await api.post('/api/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        uploadedUrls.push(response.data.url)
        toast.success('Image uploaded successfully!')
      } else {
        // Multiple files upload
        const formData = new FormData()
        files.forEach((file) => {
          formData.append('files', file)
        })
        formData.append('category', category)

        const response = await api.post('/api/upload/images', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        if (response.data.success) {
          uploadedUrls.push(...response.data.uploaded.map((f: any) => f.url))
          toast.success(`${response.data.successful} image(s) uploaded successfully!`)

          if (response.data.failed > 0) {
            toast.error(`${response.data.failed} image(s) failed to upload`)
          }
        }
      }

      // Clear previews
      previews.forEach((preview) => URL.revokeObjectURL(preview))
      setFiles([])
      setPreviews([])

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Callback with uploaded URLs
      onUploadComplete?.(uploadedUrls)
    } catch (error: any) {
      console.error('Upload error:', error)
      const errorMessage = error.response?.data?.detail || 'Failed to upload images'
      toast.error(errorMessage)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-3">
        {/* File input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
        />

        {/* Select button */}
        <label
          htmlFor="file-upload"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition"
        >
          <PhotoIcon className="h-5 w-5 mr-2" />
          Select Images
        </label>

        {/* Upload button */}
        {files.length > 0 && (
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {uploading && <Spinner size="sm" className="border-white border-t-transparent" />}
            {uploading ? 'Uploading' : `Upload ${files.length} image(s)`}
          </button>
        )}
      </div>

      {/* File info */}
      {files.length > 0 && (
        <p className="text-sm text-gray-500 mt-2">
          {files.length} file(s) selected ({maxFiles} max)
        </p>
      )}

      {/* Image previews */}
      {previews.length > 0 && (
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4 ${previewClassName}`}>
          {previews.map((preview, index) => (
            <div key={index} className="relative group">
              <img
                src={preview}
                alt={`Preview ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
              />
              {/* Remove button */}
              <button
                onClick={() => removeFile(index)}
                className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove image"
                aria-label={`Remove ${files[index].name}`}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
              {/* File name */}
              <p className="text-xs text-gray-600 mt-1 truncate">{files[index].name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ImageUpload
